/**
 * The printed downloads the Retool apps produce: a packing slip per selected bag, a thermal label
 * per selected order (TCG Order List), and the missing card list. All are client-side HTML; the
 * slips and labels are built from a read by id when pressed, so a stale page prints nothing stale.
 *
 * Kept close to the originals: warehouse staff print these, and a layout they do not recognise
 * costs them more than a tidier one gains us.
 */
import { urgentMissingCards } from "@loot-os/fulfillment-core/order/packing-fold";
import { exportFileName } from "./orderExports";
import { type Dateish, toDate } from "./packing";

/** Escapes text going into the generated markup; card names carry quotes and ampersands. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function download(html: string, name: string): void {
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = exportFileName(name, new Date(), "html");
  document.body.append(link);
  link.click();
  link.remove();
  // `click()` only queues the navigation, so revoking in the same tick races the browser
  // fetching the blob. Chrome usually wins that race; other browsers have not, and a lost race
  // downloads nothing and throws nothing.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export interface PackingSlipOrder {
  orderId: string;
  tenantReferenceId: string | null;
  shopperName: string | null;
  createdAt: Dateish;
  /** Non-cancelled lines: what the slip calls Count. */
  lineCount: number;
}

const SLIP_STYLES = `
  @media print {
    body { background-color: #FFFFFF; }
    .packing-slip { page-break-after: always; margin: 0; padding: 0; }
  }
  body { font-family: Arial, sans-serif; margin: 0; padding: 0; background-color: #f4f4f4; }
  .packing-slip {
    max-width: 600px; margin: 20px auto; padding: 20px;
    background-color: #fff; border: 1px solid #ddd; page-break-after: always;
  }
  .logo { text-align: center; margin-bottom: 20px; font-weight: bold; font-size: 20px; }
  .information { margin-bottom: 20px; }
`;

/** One slip per bag, each its own printed page. */
export function exportPackingSlips(orders: PackingSlipOrder[], brand = "Loot OS"): void {
  const slips = orders
    .map(
      (order) => `
<div class="packing-slip">
  <div class="header">
    <div class="logo">${escapeHtml(brand)}</div>
    <div class="information">
      <p><strong>Customer:</strong> ${escapeHtml(order.shopperName ?? "—")}</p>
      <p><strong>Created At:</strong> ${toDate(order.createdAt).toLocaleString()}</p>
      <p><strong>Order ID:</strong> ${escapeHtml(order.tenantReferenceId ?? order.orderId)}</p>
      <p><strong>Count:</strong> ${order.lineCount}</p>
    </div>
  </div>
</div>`,
    )
    .join("");

  download(
    `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><title>Packing Slips</title><style>${SLIP_STYLES}</style></head>
<body>${slips}</body>
</html>`,
    "export-packingSlips",
  );
}

/** One order's thermal label: what `orders.ordersExport` reads, its dates as they cross the wire. */
export interface ThermalLabelOrder {
  orderId: string;
  tenantReferenceId: string | null;
  customerName: string | null;
  streetAddress: string;
  /** The live cards, A to Z, each `name - set` with its printing. */
  cards: Array<{ name: string; printing: string | null }>;
}

const LABEL_STYLES = `${SLIP_STYLES}
  .header { border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
  .items { width: 100%; border-collapse: collapse; }
  .items th, .items td { border: 1px solid #ddd; padding: 8px; text-align: left; }
  .items th { background-color: #f8f8f8; }
`;

/**
 * TCG Order List's Export Thermal Labels: one page per order with the customer, address, order id,
 * the time it was printed (Retool's Date) and its live cards numbered A to Z. No logo and no
 * pictures, as in Retool.
 */
export function thermalLabelsHtml(orders: ThermalLabelOrder[], printedAt: Date): string {
  const labels = orders
    .map((order) => {
      const items = order.cards
        .map((card, index) => {
          const printing = card.printing ? ` (${card.printing})` : "";
          return `<tr><td>${index + 1}. ${escapeHtml(card.name + printing)}</td></tr>`;
        })
        .join("");
      return `
<div class="packing-slip">
  <div class="header">
    <div class="information">
      <p><strong>Customer:</strong> ${escapeHtml(order.customerName ?? "—")}</p>
      <p><strong>Address:</strong> ${escapeHtml(order.streetAddress || "—")}</p>
      <p><strong>Order ID:</strong> ${escapeHtml(order.tenantReferenceId ?? order.orderId)}</p>
      <p><strong>Date:</strong> ${printedAt.toLocaleString()}</p>
    </div>
  </div>
  <table class="items"><thead><tr><th>Items</th></tr></thead><tbody>${items}</tbody></table>
</div>`;
    })
    .join("");
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><title>Thermal Labels</title><style>${LABEL_STYLES}</style></head>
<body>${labels}</body>
</html>`;
}

/** Downloads {@link thermalLabelsHtml} for the orders, stamped now. */
export function exportThermalLabels(orders: ThermalLabelOrder[]): void {
  download(thermalLabelsHtml(orders, new Date()), "export-thermalLabels");
}

/** Retool fits twelve to a page, four across. */
export const MISSING_CARDS_PER_PAGE = 12;

const MISSING_STYLES = `
  @media print {
    body { margin: 0; background-color: white; }
    .page { page-break-after: always; }
    .page-content { break-inside: avoid; }
  }
  body { font-family: sans-serif; background-color: #f4f4f4; padding: 20px; }
  .page { margin-bottom: 40px; }
  .page-content { display: flex; flex-direction: column; }
  .header {
    text-align: center; font-size: 30px; font-weight: bold;
    color: #b00000; margin-bottom: 20px; text-transform: uppercase;
  }
  .card-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
  .card {
    background: white; border: 2px solid #444; padding: 10px; text-align: center;
    font-size: 14px; height: 270px; box-sizing: border-box;
    display: flex; flex-direction: column; justify-content: flex-start; overflow: hidden;
  }
  .card img { max-width: 100%; max-height: 180px; object-fit: contain; margin: 0 auto; }
  .card-name, .card-meta { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .card-name { margin-top: 10px; font-weight: bold; font-size: 16px; }
  .card-meta { font-size: 12px; color: #555; margin-top: 4px; }
`;

/**
 * The urgent poster: cards within seven days of their shipping deadline, at most 48, oldest
 * first and then by category. Returns false when that filter matches nothing, so the page can
 * say so instead of downloading an empty sheet.
 */
export function exportMissingCards(lines: Parameters<typeof urgentMissingCards>[0]): boolean {
  const cards = urgentMissingCards(lines);
  if (cards.length === 0) return false;

  const pages: string[] = [];
  for (let start = 0; start < cards.length; start += MISSING_CARDS_PER_PAGE) {
    const chunk = cards.slice(start, start + MISSING_CARDS_PER_PAGE);
    const grid = chunk
      .map((card) => {
        const name = escapeHtml(card.name);
        const image = card.imageUrl
          ? `<img src="${escapeHtml(card.imageUrl)}" alt="${name}" />`
          : "";
        const printing = card.printing ? escapeHtml(card.printing) : "";
        const created = toDate(card.createdAt).toLocaleDateString();
        const customer = card.customerName ? escapeHtml(card.customerName) : "";
        return `
        <div class="card">
          ${image}
          <div class="card-name" title="${name}">${name}</div>
          <div class="card-meta">${printing} • Needed: ${card.needed}</div>
          <div class="card-meta" title="${customer}">${created}</div>
        </div>`;
      })
      .join("");
    pages.push(`
      <div class="page">
        <div class="page-content">
          <div class="header">Urgent Missing Cards</div>
          <div class="card-grid">${grid}</div>
        </div>
      </div>`);
  }

  download(
    `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><title>Missing Cards</title><style>${MISSING_STYLES}</style></head>
<body>${pages.join("")}</body>
</html>`,
    "missing-cards",
  );
  return true;
}
